import { cloneDeep, isNumber, pull, pullAll } from 'lodash';
import { v4 as uuid } from 'uuid';
import { getBehavior, getGroupExtension, mergeMaterialIds, scaleLayer, trackConfig } from '@/framework';
import { Result } from '@/framework/plugins';
import { base64Kit } from '@/utils/base64-kit';
import { getBackgroundLayerProps, getImgLayerProps } from '@/utils/layer-kit';
import { traverseTree } from '@/utils/tree-kit';
import {
    IExportPlugin,
    IGroupLayerProps,
    IImageLayerProps,
    ILayerProps,
    ITextLayerProps,
    IVectorLayerProps,
    LayerType,
    PluginType,
} from '@/version-types';
import { getDefaultImageLayerData } from '../image-layer/constants/default-image-data';
import { getImageEffectResultImgSize } from '../image-layer/constants/image-layer-constant';
import { Layer } from '../layer';
import { MAX_CANVAS_AREA, MAX_CANVAS_DIMENSION, SCALE_WIDTH_RATIO } from '../vector-layer/constants';
import { checkIsUsingEffect } from '../vector-layer/utils';
import { AlignGroupLayers } from './align-group-layers';
import groupLayerBehavior from './behavior';
import { GroupHistory } from './group-history';
import { HistoryType } from './type';

export class GroupLayer extends Layer {
    align: AlignGroupLayers;

    constructor(identity: string) {
        super(identity);
        this.align = new AlignGroupLayers(identity);
    }

    /**
     * 检查是否是图层组
     *
     * @param layer - 图层属性
     */
    checkIsGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.checkIsGroup(layer);
    }

    /**
     * 检查是否是临时组
     *
     * @param layer - 图层属性
     */
    checkIsExtraGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.checkIsExtraGroup(layer);
    }

    /**
     * 检查是否是正式组
     *
     * @param layer - 图层属性
     */
    checkIsFormalGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.checkIsFormalGroup(layer);
    }

    /**
     * 检查是否可以拆分
     *
     * @param layer - 图层属性
     */
    checkBroken(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.checkBrokenGroup(layer);
    }

    /**
     * 检查是否是拓展图层
     *
     * @param layer - 图层属性
     */
    checkIsGroupExtension(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.checkIsGroupExtension(layer);
    }

    /**
     * 获取拓展图层
     *
     * @param layer - 图层属性
     */
    getExtension<T>(layer: ILayerProps | null) {
        if (!layer) return null;
        return getGroupExtension<T>(layer);
    }

    /**
     * 成组
     *
     * @param layerId - 图层 ID
     */
    pack(layerId: string) {
        const layer = this.framework.getLayer(layerId, true);
        if (!this.framework.checkIsExtraGroup(layer)) return;

        const originIndex = this.framework.tryGetLayerOriginIndex(layerId);
        if (!isNumber(originIndex)) return;

        const info = this.framework.tryGetOriginIdsAndIndex(originIndex);
        const originIds = info.originIds;

        const childIndexes: number[] = [];
        const childIds: string[] = [];
        layer.children.forEach((v) => {
            const id = v.id;
            if (originIds.indexOf(id) !== -1) {
                childIndexes.push(originIds.indexOf(id));
                childIds.push(id);
            }
        });

        const maxChildNextLayerId = originIds[Math.max(...childIndexes) + 1];
        pullAll(originIds, childIds);
        if (maxChildNextLayerId !== layerId) {
            pull(originIds, layerId);
            const insertIndex = originIds.indexOf(maxChildNextLayerId);
            originIds.splice(insertIndex, 0, layerId);
        }
        (layer.data as IGroupLayerProps).isExtraGroup = false;
        this.framework.stage.addOrUpdate(layer, true, originIndex);
        this.framework.history.write({
            type: HistoryType.PACK,
            data: {
                item: {
                    childIndexes,
                    childIds,
                    layer: cloneDeep(layer),
                    layerIndex: originIds.indexOf(layer.id),
                },
            },
            trackFunc: trackConfig[LayerType.GROUP_LAYER]?.group,
            needSyncRenderIds: true,
        });
        this.framework.operate.setActiveLayer(layerId);
    }

    /**
     * 解组
     *
     * @param layerId - 图层 ID
     */
    unpack(layerId: string) {
        const layer = this.framework.getLayer(layerId, true);
        if (!this.framework.checkIsFormalGroup(layer) || !this.framework.checkBrokenGroup(layer)) return;

        const originIndex = this.framework.tryGetLayerOriginIndex(layerId);
        if (!isNumber(originIndex)) return;

        const info = this.framework.tryGetOriginIdsAndIndex(originIndex);
        const originIds = info.originIds;

        const layerIndex = originIds.indexOf(layerId);
        if (layerIndex === -1) return;

        const childIds: string[] = (layer.children || []).map((v) => v.id);
        if (childIds.length === 0) return;

        pull(originIds, layerId);
        originIds.splice(layerIndex, 0, ...childIds);
        originIds.push(layerId);

        const oldLayer = cloneDeep(layer);
        (oldLayer.data as IGroupLayerProps).isExtraGroup = true;
        (layer.data as IGroupLayerProps).isExtraGroup = true;
        layer.lock = false;
        this.framework.stage.addOrUpdate(layer, true, originIndex);
        this.framework.history.write({
            type: HistoryType.UNPACK,
            data: {
                item: {
                    layer: oldLayer,
                    layerIndex: originIds.indexOf(layer.id),
                },
            },
            trackFunc: trackConfig[LayerType.GROUP_LAYER]?.unGroup,
            needSyncRenderIds: true,
        });
        this.framework.operate.setActiveLayer(layerId);
    }
    /**
     * 合成图片
     *
     * @param layerId - 图层 ID
     */
    async merge(layerId: string) {
        const layer = this.framework.getLayer(layerId, true);
        if (!layer || layer.lock) return;
        const imageLayer = await this.getMergeResult(layer);
        const latestLayer = this.framework.getLayer(layerId);
        if (!this.framework.checkLayerEqual(layer, latestLayer)) {
            console.log('ot 图层不一致，丢弃');
            return;
        }
        const delData = this.framework.operate.delLayer(layerId);
        const insertData = this.framework.stage.addOrUpdate(imageLayer);
        this.framework.operate.setActiveLayer(insertData.id);
        if (delData) {
            // const url = await base64Kit.getRemoteUrlAsync((insertData.data as IImageLayerProps).url);
            // (insertData.data as IImageLayerProps).url = url;
            this.framework.history.write({
                type: HistoryType.MERGE,
                data: {
                    delData,
                    insertData,
                },
                needSyncRenderIds: true,
                // trackFunc: trackFunc || getTrackFunc(layer, 'merge'),
            });
        }
    }

    /**
     * 获取合并的图层数据
     *
     * 合成画布尺寸为 resolution 倍数下的 outerWidth × outerHeight；若子图层含特效，
     * 会再按 imgEffectResultImgSize 外扩（与 resolution 叠乘）。大尺寸组在特效外扩后
     * 可能超过浏览器 Canvas 面积/单边上限，导致 drawLayers 静默失败，故需动态收敛。
     *
     * @param layer - 图层属性
     * @param type - 可选；图片格式；默认值为 `png`
     * @param resolution - 可选；指定图片分辨率倍数；默认值为 `2` 为二倍图
     */
    async getMergeResult(layer: ILayerProps, type = 'png', resolution = 2) {
        const newLayer = cloneDeep(layer);
        const { top, left, width, height } = newLayer;
        let materialIds = {};
        let needExpand = false;
        const resolutionWidth = width * resolution;
        const resolutionHeight = height * resolution;
        let outerWidth = resolutionWidth;
        let outerHeight = resolutionHeight;
        const layers = [newLayer];
        let imgEffectResultImgSize = 1.4;
        // 判断是否应用特效
        traverseTree(newLayer, (item: ILayerProps) => {
            // 图片特效
            if (item.layerType === LayerType.IMAGE_LAYER) {
                const data = item.data as IImageLayerProps;
                const { effect } = data;
                if (effect?.screenshotUrl) {
                    let scale = 0;

                    needExpand = true;
                    if (effect.effectPosition) {
                        const w = effect.effectPosition.right - effect.effectPosition.left;
                        const h = effect.effectPosition.bottom - effect.effectPosition.top;
                        scale = w / item.width > h / item.height ? w / item.width : h / item.height;
                    } else {
                        scale = getImageEffectResultImgSize(data);
                    }
                    imgEffectResultImgSize = scale > imgEffectResultImgSize ? scale : imgEffectResultImgSize;
                }
            }
            if (item.layerType === LayerType.TEXT_LAYER) {
                const data = item.data as ITextLayerProps;
                // 文字划重点
                if (data.textMark) {
                    needExpand = true;
                }
                data.textContents?.forEach((text) => {
                    if (text.textMark) {
                        needExpand = true;
                    }
                });
                // 文字特效有描边/阴影/外发光/特效花字
                if (
                    data.textStroke?.length ||
                    data.textGlow?.length ||
                    data.textShadow?.length ||
                    data.advancedTextMaterial
                ) {
                    needExpand = true;
                }
            }
            //  SVG 特效
            if (item.layerType === LayerType.VECTOR_LAYER) {
                const layerData = item.data as IVectorLayerProps;
                const isUsingEffect = checkIsUsingEffect(layerData, {
                    includeBlur: true,
                });
                if (isUsingEffect) {
                    needExpand = true;
                    imgEffectResultImgSize = Math.max(imgEffectResultImgSize, SCALE_WIDTH_RATIO); // SVG 特效至少2倍外扩
                }
            }
        });

        // 缩放图层
        scaleLayer(this.identity, newLayer, width * (resolution - 1), undefined, undefined, true);

        // 如果有图片特效则将扩大尺寸，并重新计算位置
        if (needExpand) {
            outerWidth = Math.floor(outerWidth * imgEffectResultImgSize);
            outerHeight = Math.floor(outerHeight * imgEffectResultImgSize);

            // 大图 + 二倍图 + 特效外扩时 canvas 易超限（如 7900×4211 → 31600×16844），
            // 按面积与单边上限等比缩小 outer 尺寸，保证 Result.drawLayers 可正常绘制
            if (
                outerWidth * outerHeight > MAX_CANVAS_AREA ||
                outerWidth > MAX_CANVAS_DIMENSION ||
                outerHeight > MAX_CANVAS_DIMENSION
            ) {
                const areaScale = Math.sqrt(MAX_CANVAS_AREA / (outerWidth * outerHeight));
                const dimScale = Math.min(MAX_CANVAS_DIMENSION / outerWidth, MAX_CANVAS_DIMENSION / outerHeight);
                const safeScale = Math.min(areaScale, dimScale);
                outerWidth = Math.floor(outerWidth * safeScale);
                outerHeight = Math.floor(outerHeight * safeScale);
            }

            const backgroundLayer = getBackgroundLayerProps({
                width: outerWidth,
                height: outerHeight,
                data: {
                    backgroundColor: '',
                    backgroundGradient: null,
                    cutoutMaskURL: '',
                    imageTransform: {
                        flipH: false,
                        flipV: false,
                        heightRatio: 1,
                        offsetX: 0,
                        offsetY: 0,
                        rotate: 0,
                        scale: 1,
                        widthRatio: 1,
                    },
                    maskType: 0,
                    maskURL: '',
                    naturalHeight: 0,
                    naturalWidth: 0,
                    stretchOption: 2,
                    type: '',
                    url: '',
                },
            });

            layers.unshift(backgroundLayer);
        }

        traverseTree(newLayer, (item: ILayerProps) => {
            item.left = item.left - left + (outerWidth - resolutionWidth) / 2;
            item.top = item.top - top + (outerHeight - resolutionHeight) / 2;
            materialIds = mergeMaterialIds(materialIds, item.materialIds);
        });

        const canvas = await this.framework.getInstance(Result).drawLayers(layers, type);

        const layerWidth = width * (outerWidth / resolutionWidth);
        const layerHeight = height * (outerHeight / resolutionHeight);

        const imageLayerData = getDefaultImageLayerData(
            base64Kit.getCode(canvas.toDataURL(), uuid()),
            layerWidth,
            layerHeight,
            layer.left - (layerWidth - width) / 2,
            layer.top - (layerHeight - height) / 2,
            materialIds,
            outerWidth,
            outerHeight,
        );

        return getImgLayerProps(imageLayerData);
    }

    /**
     * 创建临时组并激活
     *
     * @param childIds - 子图层 ID 数组
     */
    createGroupAndActive(childIds: string[]) {
        const finalChildIds: string[] = [];
        for (let i = 0; i < childIds.length; i++) {
            const id = childIds[i];
            const layer = this.framework.getLayer(id);
            if (!getBehavior(this.identity, layer)?.checkDisableInGroup?.(layer)) {
                finalChildIds.push(id);
            }
        }
        if (finalChildIds.length <= 1) return;
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const groupLayer = this.framework.tryCreateGroup(originIndex, uuid(), new Set(finalChildIds));
        if (!groupLayer) return;
        this.framework.stage.addOrUpdate(groupLayer);
        this.framework.operate.setActiveLayer(groupLayer.id);
        this.framework.operate.delHoverId();
    }
}

/**
 * 组图层插件
 */
export const groupLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.GROUP_LAYER,
    createInstance({ identity }) {
        return [{ key: 'GroupLayer', value: new GroupLayer(identity) }];
    },
    createBehavior() {
        return groupLayerBehavior;
    },
    createHistory({ identity }) {
        return [new GroupHistory(identity)];
    },
};
